import { Observable, of, zip } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { IEntitlement, IPermission } from '../models';
import { CentralApis, Endpoint, PageConfig } from '../interfaces/internal';
import { PageTitles } from '../interfaces/types';
import { Page } from './page';
import { PermissionDTO } from '../interfaces/dtos';
import { Result } from '../interfaces/helpers';
import { Entitlement } from '../models/entitlement';

export class EntitlementPage extends Page<Entitlement> {
  permissionUrl: string;

  constructor({ endpoint }: Endpoint, config: PageConfig) {
    super((endpoint as CentralApis).iam, PageTitles.entitlement, config);
  }

  addRole(permission: PermissionDTO): Observable<IPermission> {
    return this.dataService
      .postDTO<IPermission, PermissionDTO>(this.permissionUrl, permission)
      .pipe(map((result) => result));
  }

  postEntitlement(userRoleId: string, privilegeId: string) {
    const postEntitlementUrl =
      this.url + `userRole/${userRoleId}/entitlement/${privilegeId}`;
    return this.dataService.post(postEntitlementUrl, null);
  }

  deleteEntitlement(userRoleId: string, privilegeId: string) {
    const removeEntitlementUrl =
      this.url + `userRole/${userRoleId}/entitlement/${privilegeId}`;
    return this.dataService.delete(removeEntitlementUrl);
  }

  getPermission(userName: string): Observable<IEntitlement> {
    return this.dataService
      .get<IEntitlement>(this.permissionUrl, { userName })
      .pipe(map((result) => result));
  }

  fetch(queryString?): Observable<Result<Entitlement>> {
    return this.getItems(queryString).pipe(
      tap(({ items }) => {
        this.pageItems.next(items);
      })
    );
  }
  updateRole(
    permission: PermissionDTO,
    privilegeID
  ): Observable<PermissionDTO> {
    return this.dataService
      .put<PermissionDTO>(this.permissionUrl + '/' + privilegeID, permission)
      .pipe(map((result) => result));
  }

  fetchById(id: string): Observable<Entitlement> {
    return this.getItem(id).pipe(
      map(
        (data: IEntitlement) =>
          new Entitlement({
            id: data.id,
            createdAt: data.createdAt,
            modifiedAt: data.modifiedAt,
            createdBy: data.createdBy,
            modifiedBy: data.modifiedBy,
            href: data.href,
            privilegeId: data.privilegeId,
            userRoleId: data.userRoleId,
          })
      )
    );
  }

  update(entitlement: Entitlement): Observable<Entitlement> {
    let updatedEntitlements: Entitlement[];

    return this.items.pipe(
      take(1),
      switchMap((entitlements) => {
        if (!entitlements || entitlements.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(entitlements);
        }
      }),
      switchMap((entitlements) => {
        const entitlementIndex = entitlements.findIndex(
          (i) => i.id === entitlement.id
        );
        updatedEntitlements = [...entitlements];
        updatedEntitlements[entitlementIndex] = this.createFromDTO(entitlement);
        return this.updateItem(
          {
            userName: updatedEntitlements[entitlementIndex].privilegeId,
          },
          updatedEntitlements[entitlementIndex].id
        );
      }),
      tap(() => {
        this.pageItems.next(updatedEntitlements);
      })
    );
  }

  createFromDTO(item): Entitlement {
    return new Entitlement({
      id: item.id,
      createdAt: item.createdAt,
      modifiedAt: item.modifiedAt,
      createdBy: item.createdBy,
      modifiedBy: item.modifiedBy,
      userRoleId: item.userRoleId,
      privilegeId: item.privilegeId,
    });
  }
}
